import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { inr } from "@/lib/format";
import { Skeleton, StatCard } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Download, Play, Sparkles, Trash2, Upload, Plus } from "lucide-react";
import ReactMarkdown from "react-markdown";

const inp = "h-9 rounded-md border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary";

export const Reconciliation = () => {
  const [waivers, setWaivers] = useState(null);
  const [latest, setLatest] = useState(null);
  const [running, setRunning] = useState(false);
  const [params, setParams] = useState({ start: "2026-06-01", end: "2026-06-30", baseline_start: "2026-05-16", baseline_end: "2026-05-31" });
  const [showAdd, setShowAdd] = useState(false);
  const [nw, setNw] = useState({ fee_component: "referral", rate: 2.5, price_min: 1000, price_max: "", start: "2026-06-01", end: "2026-06-30", keyword: "", note: "" });
  const [aiText, setAiText] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const fileRef = useRef();

  const load = () => {
    api.get("/tools/reco/waivers").then(({ data }) => setWaivers(data.waivers));
    api.get("/tools/reco/latest").then(({ data }) => {
      setLatest(data.result);
      setRunning(!!data.state?.running);
      if (data.result?.ai_summary) setAiText(data.result.ai_summary);
    });
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!running) return;
    const t = setInterval(async () => {
      const { data } = await api.get("/tools/reco/latest");
      if (!data.state?.running) {
        clearInterval(t);
        setRunning(false);
        if (data.state?.error) toast.error(`Reconciliation failed: ${data.state.error}`);
        else toast.success("Reconciliation complete");
        setLatest(data.result);
      }
    }, 5000);
    return () => clearInterval(t);
  }, [running]);

  const run = async () => {
    await api.post("/tools/reco/run", params);
    setRunning(true);
    toast.info("Reconciliation running — comparing settlement charges against waiver rates…");
  };

  const addWaiver = async () => {
    const body = { ...nw, rate: parseFloat(nw.rate), price_min: parseFloat(nw.price_min || 0), price_max: nw.price_max ? parseFloat(nw.price_max) : null };
    await api.post("/tools/reco/waivers", body);
    toast.success("Waiver added");
    setShowAdd(false);
    load();
  };

  const uploadStd = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/tools/reco/std-whf", fd);
      toast.success(`${data.uploaded} std WHF rates uploaded — re-run reconciliation to apply`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
    e.target.value = "";
  };

  const exportExcel = async () => {
    const res = await api.get("/tools/reco/export", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `amazon-fee-reconciliation-${new Date().toISOString().slice(0, 10)}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const aiSummary = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post("/tools/reco/ai-summary");
      setAiText(data.summary);
    } catch {
      toast.error("AI summary failed");
    } finally {
      setAiLoading(false);
    }
  };

  if (!waivers) return <Skeleton className="h-64" />;
  const s = latest?.summary;

  const SkuTable = ({ rows, cols, testId }) => (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid={testId}>
        <thead className="bg-muted/60 text-xs uppercase tracking-wider text-muted-foreground">
          <tr>{cols.map((c) => <th key={c.h} className="text-left px-4 py-3 font-semibold">{c.h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.slice(0, 100).map((r) => (
            <tr key={r.sku} className={`border-t border-border ${r.claimable > 0 ? "bg-red-50/60 dark:bg-red-950/20" : ""}`}>
              {cols.map((c) => <td key={c.h} className="px-4 py-2.5">{c.get(r)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > 100 && (
        <p className="px-4 py-2.5 text-xs text-muted-foreground border-t border-border">
          Showing top 100 of {rows.length} SKUs (sorted by claimable) — use "Export Excel" for the full list.
        </p>
      )}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="reconciliation">
      <section className="bg-card border border-border rounded-lg shadow-sm">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-heading text-base font-semibold tracking-tight">Waiver rules ({waivers.length})</h3>
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => fileRef.current.click()} data-testid="std-whf-upload-button">
              <Upload className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" /> Upload std WHF (CSV/Excel)
            </Button>
            <input ref={fileRef} type="file" accept=".csv,.xlsx" className="hidden" onChange={uploadStd} data-testid="std-whf-file-input" />
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => setShowAdd(!showAdd)} data-testid="add-waiver-button">
              <Plus className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" /> Add waiver
            </Button>
          </div>
        </div>
        {showAdd && (
          <div className="p-4 border-b border-border grid grid-cols-2 md:grid-cols-4 gap-3 text-sm" data-testid="waiver-form">
            <select className={inp} value={nw.fee_component} onChange={(e) => setNw({ ...nw, fee_component: e.target.value })} data-testid="waiver-component">
              <option value="referral">Referral Fees (% of price)</option>
              <option value="whf">Weight Handling (% of std WHF)</option>
            </select>
            <input className={inp} type="number" step="0.1" placeholder="Rate %" value={nw.rate} onChange={(e) => setNw({ ...nw, rate: e.target.value })} data-testid="waiver-rate" />
            <input className={inp} type="number" placeholder="Item price min" value={nw.price_min} onChange={(e) => setNw({ ...nw, price_min: e.target.value })} data-testid="waiver-price-min" />
            <input className={inp} type="number" placeholder="Item price max (blank = none)" value={nw.price_max} onChange={(e) => setNw({ ...nw, price_max: e.target.value })} data-testid="waiver-price-max" />
            <input className={inp} type="date" value={nw.start} onChange={(e) => setNw({ ...nw, start: e.target.value })} data-testid="waiver-start" />
            <input className={inp} type="date" value={nw.end} onChange={(e) => setNw({ ...nw, end: e.target.value })} data-testid="waiver-end" />
            <input className={inp} placeholder="Title keyword (e.g. walker)" value={nw.keyword} onChange={(e) => setNw({ ...nw, keyword: e.target.value })} data-testid="waiver-keyword" />
            <input className={inp} placeholder="Note" value={nw.note} onChange={(e) => setNw({ ...nw, note: e.target.value })} data-testid="waiver-note" />
            <Button size="sm" className="rounded-full col-span-2" onClick={addWaiver} data-testid="save-waiver-button">Save waiver</Button>
          </div>
        )}
        <table className="w-full text-sm">
          <thead className="bg-muted/60 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>{["Component", "Rate", "Item price band", "Period", "Keyword", "Note", ""].map((h) => <th key={h} className="text-left px-4 py-2.5 font-semibold">{h}</th>)}</tr>
          </thead>
          <tbody>
            {waivers.map((w) => (
              <tr key={w.waiver_id} className="border-t border-border" data-testid={`waiver-row-${w.waiver_id}`}>
                <td className="px-4 py-2">
                  <Badge className={`border-0 font-semibold ${w.fee_component === "referral" ? "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400" : "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400"}`}>
                    {w.fee_component === "referral" ? "Referral" : "WHF"}
                  </Badge>
                </td>
                <td className="px-4 py-2 font-semibold">{w.rate}%{w.fee_component === "whf" ? " of std" : ""}</td>
                <td className="px-4 py-2 text-xs">₹{w.price_min ?? 0} – {w.price_max ? `₹${w.price_max}` : "∞"}</td>
                <td className="px-4 py-2 text-xs">{w.start} → {w.end}</td>
                <td className="px-4 py-2 text-xs">{w.keyword || "—"}</td>
                <td className="px-4 py-2 text-xs text-muted-foreground max-w-xs truncate">{w.note}</td>
                <td className="px-4 py-2">
                  <button onClick={async () => {
                    try { await api.delete(`/tools/reco/waivers/${w.waiver_id}`); load(); }
                    catch { toast.error("Delete failed"); }
                  }}
                    className="text-muted-foreground hover:text-red-600" data-testid={`delete-waiver-${w.waiver_id}`}>
                    <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="bg-card border border-border rounded-lg shadow-sm p-4 flex items-end gap-3 flex-wrap" data-testid="reco-run-panel">
        {[["start", "Promo start"], ["end", "Promo end"], ["baseline_start", "Baseline start (std WHF)"], ["baseline_end", "Baseline end"]].map(([k, label]) => (
          <label key={k} className="text-xs text-muted-foreground space-y-1">
            <span>{label}</span>
            <input type="date" className={`${inp} block`} value={params[k]} onChange={(e) => setParams({ ...params, [k]: e.target.value })} data-testid={`reco-${k}`} />
          </label>
        ))}
        <Button className="rounded-full" onClick={run} disabled={running} data-testid="reco-run-button">
          <Play className="h-4 w-4 mr-1.5" aria-hidden="true" /> {running ? "Reconciling…" : "Run reconciliation"}
        </Button>
        {latest && (
          <>
            <Button variant="outline" className="rounded-full" onClick={exportExcel} data-testid="reco-export-button">
              <Download className="h-4 w-4 mr-1.5" aria-hidden="true" /> Export Excel
            </Button>
            <Button variant="outline" className="rounded-full" onClick={aiSummary} disabled={aiLoading} data-testid="reco-ai-button">
              <Sparkles className="h-4 w-4 mr-1.5" aria-hidden="true" /> {aiLoading ? "Analysing…" : "AI summary + dispute email"}
            </Button>
          </>
        )}
      </section>

      {latest && s && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <StatCard label="Items checked" value={s.items_checked.toLocaleString("en-IN")} sub={`run ${latest.run_at?.slice(0, 10)}`} testId="reco-items-kpi" />
            <StatCard label="Referral overcharged" value={s.referral.over_items.toLocaleString("en-IN")} sub={`of ${s.referral.items.toLocaleString("en-IN")} items`} testId="reco-ref-over-kpi" />
            <StatCard label="Referral claimable" value={inr(s.referral.claimable)} testId="reco-ref-claim-kpi" />
            <StatCard label="WHF overcharged" value={s.whf.over_items.toLocaleString("en-IN")} sub={`${s.whf.no_baseline_items} items lack std WHF`} testId="reco-whf-over-kpi" />
            <StatCard label="WHF claimable" value={inr(s.whf.claimable)} testId="reco-whf-claim-kpi" />
          </div>

          <section className="bg-card border border-border rounded-lg shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-border">
              <h3 className="font-heading text-base font-semibold tracking-tight">Referral Fees by SKU <span className="text-xs font-normal text-muted-foreground">(red rows = overcharged vs waiver)</span></h3>
            </div>
            <SkuTable rows={latest.referral_skus} testId="reco-ref-table" cols={[
              { h: "Product", get: (r) => <span className="font-medium">{r.title || r.sku}</span> },
              { h: "SKU", get: (r) => <span className="font-mono text-xs text-muted-foreground">{r.sku}</span> },
              { h: "Waiver %", get: (r) => `${r.rate}%` },
              { h: "Actual %", get: (r) => <span className={r.actual_pct > r.rate + 0.15 ? "text-red-600 font-bold" : ""}>{r.actual_pct}%</span> },
              { h: "Units", get: (r) => r.units },
              { h: "Principal", get: (r) => inr(r.principal) },
              { h: "Charged", get: (r) => inr(r.actual) },
              { h: "Expected", get: (r) => inr(r.expected) },
              { h: "Claimable", get: (r) => <strong className={r.claimable > 0 ? "text-red-600" : ""}>{inr(r.claimable)}</strong> },
            ]} />
          </section>

          <section className="bg-card border border-border rounded-lg shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-border">
              <h3 className="font-heading text-base font-semibold tracking-tight">Weight Handling Fees by SKU <span className="text-xs font-normal text-muted-foreground">(std WHF from {s.uploaded_std_skus ? `${s.uploaded_std_skus} uploaded + ` : ""}{s.baseline_skus} baseline SKUs)</span></h3>
            </div>
            <SkuTable rows={latest.whf_skus} testId="reco-whf-table" cols={[
              { h: "Product", get: (r) => <span className="font-medium">{r.title || r.sku}</span> },
              { h: "SKU", get: (r) => <span className="font-mono text-xs text-muted-foreground">{r.sku}</span> },
              { h: "Waiver", get: (r) => `${r.rate}% of std` },
              { h: "Std/unit", get: (r) => <span>{inr(r.std_pu)} <span className="text-[10px] text-muted-foreground">({r.std_source})</span></span> },
              { h: "Actual/unit", get: (r) => <span className={r.claimable > 0 ? "text-red-600 font-bold" : ""}>{inr(r.actual_pu)}</span> },
              { h: "Units", get: (r) => r.units },
              { h: "Charged", get: (r) => inr(r.actual) },
              { h: "Expected", get: (r) => inr(r.expected) },
              { h: "Claimable", get: (r) => <strong className={r.claimable > 0 ? "text-red-600" : ""}>{inr(r.claimable)}</strong> },
            ]} />
          </section>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {[["Highest effective Referral %", latest.high_referral, (r) => `${r.actual_pct}%`, "high-ref"],
              ["Highest WHF per unit", latest.high_whf, (r) => inr(r.actual_pu), "high-whf"]].map(([title, rows, val, tid]) => (
              <section key={tid} className="bg-card border border-border rounded-lg shadow-sm overflow-hidden" data-testid={`reco-${tid}`}>
                <div className="px-5 py-3 border-b border-border"><h3 className="font-heading text-sm font-semibold">{title}</h3></div>
                <table className="w-full text-sm">
                  <tbody>
                    {(rows || []).slice(0, 10).map((r) => (
                      <tr key={r.sku} className="border-t border-border">
                        <td className="px-4 py-2 max-w-[16rem] truncate text-xs font-medium">{r.title || r.sku}</td>
                        <td className="px-4 py-2 text-xs text-muted-foreground">{r.units} u</td>
                        <td className="px-4 py-2 text-right font-bold text-amber-600">{val(r)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            ))}
          </div>

          {aiText && (
            <section className="bg-card border border-border rounded-lg shadow-sm p-6 prose prose-sm dark:prose-invert max-w-none" data-testid="reco-ai-summary">
              <ReactMarkdown>{aiText}</ReactMarkdown>
            </section>
          )}
        </>
      )}
      {!latest && <p className="p-6 text-sm text-muted-foreground bg-card border border-border rounded-lg">No reconciliation run yet — set the promo period (defaults to your June 2026 waivers) and click "Run reconciliation".</p>}
    </div>
  );
};
